<template>
  <div style="height: 100%; position: relative;">
    <DashboardFilterChip :chip-color="chipColor" :chip-text="chipText"></DashboardFilterChip>
    <v-card style="height: 100%;">
      <v-card-text>
        <v-row class="mt-3">
          <v-col cols="12" align="center">
            <h1>
              Gross Profit
            </h1>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="5" class="px-sm-0" align="center">
            <v-btn
              depressed
              :color="yearButtonColor"
              :disabled="disableButtons"
              @click="changeFilterButtonColor"
              >
                Year
              </v-btn>
            <v-btn
              depressed
              :color="monthButtonColor"
              :disabled="disableButtons"
              @click="changeFilterButtonColor"
              >
                Month
              </v-btn>
          </v-col>
          <v-col cols="12" sm="6" md="7" lg="6" align="center">
            <YearsProgressiveListDropDown
              v-if="filterToggle === YEAR"
              :year="yearFilter"
              :dense="true"
              :hideDetails="true"
              :disabled="disableButtons"
              @yearChange="yearFilterChangeHandler"
            ></YearsProgressiveListDropDown>
            <MonthPicker
              v-if="filterToggle === MONTH"
              label="Month"
              :year="yearFilter"
              :month="monthFilter"
              :dense="true"
              :hideDetails="true"
              :disabled="disableButtons"
              @yearChange="(newValue) => this.yearFilter = parseInt(newValue)"
              @monthChange="(newValue) => this.monthFilter = parseInt(newValue)"
              @periodChange="periodFilterChangeHandler"
            ></MonthPicker>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="px-5">
            <v-row justify="end" class="mb-2">
              <v-col cols="auto">
                <ButtonCustomIcon
                  icon="cloud_download"
                  color="primary"
                  text="Excel"
                  :disabled="disableButtons || grossProfitItems.length === 0"
                  @click="handleExportToExcel"
                />
              </v-col>
            </v-row>
            <v-data-table
              :headers="tableHeaders"
              :items="grossProfitItems"
              :calculate-widths="true"
              :items-per-page="5"
              style="height: 380px; overflow: auto;"
            >
              <template v-slot:item.grossProfit="{ item }">
                {{ item.grossProfit | currency }}
              </template>
              <template v-slot:item.commissionDetails="{ item }">
                <v-btn
                  class="mx-1 my-1 hide-on-print"
                  color="primary"
                  outlined
                  :disabled="disableButtons"
                  @click.prevent="handleShowCommissionDetails($event, item)"
                >
                  <v-icon color="primary">paid</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" align="end">
            <v-btn
              v-if="!showFetchingDataGIF"
              color="primary"
              outlined
              class="ml-2"
              @click="refreshTile"
              style="float: left;"
              >
                <v-icon>refresh</v-icon>
            </v-btn>
            <i
              v-if="showFetchingDataGIF"
              class="mr-2"
            >
              Fetching Data
            </i>
            <v-progress-circular
              v-if="showFetchingDataGIF"
              :size="30"
              :width="7"
              indeterminate
              color="primary"
            ></v-progress-circular>
            <i
              v-if="lastRefreshTime"
              class="ml-10 mr-5"
            >
              Last Update: {{ lastRefreshTime }}
            </i>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { getLocalTimeString } from '@/lib/display'
import { mapActions } from 'vuex'
import { isJSONString } from '@/lib/miscellaneous'
import { YearsProgressiveListDropDown } from '@/components/years'
import { MonthPicker } from '@/components/shared/datePickers'
import { commissionDetailsReportRoute } from '@/lib/constants/routes'
import DashboardFilterChip from '@/components/dashboard/DashboardFilterChip'
import { ButtonCustomIcon } from '@/components/buttons'
import excel from '@/lib/export/excel'
import { buildGrossProfitExport } from './grossProfitExportHelpers'
import { notifyError } from '@/lib/notifications'

export default {
  components: {
    DashboardFilterChip,
    YearsProgressiveListDropDown,
    MonthPicker,
    ButtonCustomIcon
  },
  props: {
    businessEntityId: {
      type: Number,
      default: null
    },
    chipText: {
      type: String,
      default: 'All Business Entity'
    },
    chipColor: {
      type: String,
      default: 'primary'
    }
  },
  data () {
    return {
      YEAR: 1,
      MONTH: 2,
      filterToggle: 1,
      monthButtonColor: '',
      yearButtonColor: 'primary',
      grossProfitItems: [],
      yearFilter: new Date().getFullYear(),
      monthFilter: (new Date().getMonth() + 1) < 10
        ? '0'.concat((new Date().getMonth() + 1).toString())
        : (new Date().getMonth() + 1).toString(),
      lastRefreshTime: null,
      showFetchingDataGIF: false,
      disableButtons: false
    }
  },
  computed: {
    tableHeaders: {
      get () {
        let headers = [
          { text: 'Employee', value: 'employeeName', width: '50%', class: 'text-center', cellClass: 'text-left' },
          { text: 'GP', value: 'grossProfit', width: '20%', class: 'text-center', cellClass: 'text-left' }
        ]

        if (this.filterToggle === this.MONTH) {
          headers.push({ text: 'Details', value: 'commissionDetails', width: '30%', align: 'center', sortable: false })
        }

        return headers
      }
    }
  },
  methods: {
    ...mapActions('dashboard', [
      'getGrossProfitDetailsByYear',
      'getGrossProfitDetailsByMonth'
    ]),
    changeFilterButtonColor () {
      this.monthButtonColor = this.monthButtonColor === 'primary' ? '' : 'primary'
      this.yearButtonColor = this.yearButtonColor === 'primary' ? '' : 'primary'
      this.filterToggle = this.monthButtonColor === 'primary' ? this.MONTH : this.YEAR
      if (this.yearFilter === new Date().getFullYear()) {
        this.monthFilter = new Date().getMonth() + 1
      }
      this.refreshTile()
    },
    yearFilterChangeHandler (newValue) {
      this.yearFilter = newValue
      this.refreshTile()
    },
    periodFilterChangeHandler () {
      this.refreshTile()
    },
    handleExportToExcel () {
      if (!this.grossProfitItems.length) {
        return
      }

      const params = buildGrossProfitExport({
        items: this.grossProfitItems,
        headers: this.tableHeaders,
        isMonthly: this.filterToggle === this.MONTH,
        year: this.yearFilter,
        month: this.monthFilter
      })
      excel.exportArray2Excel(params)
    },
    handleShowCommissionDetails (event, item) {
      const routeData = this.$router.resolve({
        name: commissionDetailsReportRoute.name,
        query: {
          user: item.employeeUserId,
          year: item.year,
          month: item.month
        }
      })
      if (event.ctrlKey === true) {
        window.open(routeData.href, '_blank')
      } else {
        window.open(routeData.href, '_self')
      }
    },
    saveInCache (data, filterToggle, yearFilter, monthFilter) {
      let dateTimeNow = new Date()
      this.grossProfitItems = data
      localStorage.setItem('dashboard:gross-profit-tile-filter-toggle', JSON.stringify(filterToggle))
      localStorage.setItem('dashboard:gross-profit-tile-year-filter', JSON.stringify(yearFilter))
      localStorage.setItem('dashboard:gross-profit-tile-month-filter', JSON.stringify(monthFilter))
      localStorage.setItem('dashboard:gross-profit-tile-data', JSON.stringify(data))
      localStorage.setItem('dashboard:gross-profit-tile-last-refresh-time', JSON.stringify(dateTimeNow.toUTCString()))
      this.lastRefreshTime = getLocalTimeString(dateTimeNow)
    },
    async refreshTile () {
      this.showFetchingDataGIF = true
      this.disableButtons = true
      this.grossProfitItems = []

      const filterToggle = this.filterToggle
      const yearFilter = this.yearFilter
      const monthFilter = this.monthFilter
      localStorage.setItem('dashboard:gross-profit-tile-data', JSON.stringify([]))
      localStorage.setItem('dashboard:gross-profit-tile-filter-toggle', JSON.stringify(filterToggle))
      localStorage.setItem('dashboard:gross-profit-tile-year-filter', JSON.stringify(yearFilter))
      localStorage.setItem('dashboard:gross-profit-tile-month-filter', JSON.stringify(monthFilter))

      try {
        const parameters = { year: yearFilter, businessEntityId: this.businessEntityId }
        const data = filterToggle === this.MONTH
          ? await this.getGrossProfitDetailsByMonth({ ...parameters, month: monthFilter })
          : await this.getGrossProfitDetailsByYear(parameters)
        this.saveInCache(Array.isArray(data) ? data : [], filterToggle, yearFilter, monthFilter)
      } catch (error) {
        localStorage.removeItem('dashboard:gross-profit-tile-last-refresh-time')
        this.lastRefreshTime = null
        notifyError(this.$store.dispatch, (error && error.message) || 'Unable to load gross profit details.')
      } finally {
        this.showFetchingDataGIF = false
        this.disableButtons = false
      }
    }
  },
  created () {
    if (localStorage.getItem('dashboard:gross-profit-tile-filter-toggle') && isJSONString(localStorage.getItem('dashboard:gross-profit-tile-filter-toggle'))) {
      this.filterToggle = JSON.parse(localStorage.getItem('dashboard:gross-profit-tile-filter-toggle'))
      this.monthButtonColor = this.filterToggle === this.YEAR ? '' : 'primary'
      this.yearButtonColor = this.filterToggle === this.YEAR ? 'primary' : ''
    }
    if (localStorage.getItem('dashboard:gross-profit-tile-year-filter') && isJSONString(localStorage.getItem('dashboard:gross-profit-tile-year-filter'))) {
      this.yearFilter = JSON.parse(localStorage.getItem('dashboard:gross-profit-tile-year-filter'))
    }
    if (localStorage.getItem('dashboard:gross-profit-tile-month-filter') && isJSONString(localStorage.getItem('dashboard:gross-profit-tile-month-filter'))) {
      this.monthFilter = JSON.parse(localStorage.getItem('dashboard:gross-profit-tile-month-filter'))
    }
    if (localStorage.getItem('dashboard:gross-profit-tile-data') && isJSONString(localStorage.getItem('dashboard:gross-profit-tile-data'))) {
      this.grossProfitItems = JSON.parse(localStorage.getItem('dashboard:gross-profit-tile-data'))
    }
    if (localStorage.getItem('dashboard:gross-profit-tile-last-refresh-time') && isJSONString(localStorage.getItem('dashboard:gross-profit-tile-last-refresh-time'))) {
      let lastRefreshTime = new Date(JSON.parse(localStorage.getItem('dashboard:gross-profit-tile-last-refresh-time')))
      this.lastRefreshTime = getLocalTimeString(lastRefreshTime)
    }
    if ((this.filterToggle && this.filterToggle === this.YEAR && this.yearFilter === new Date().getFullYear()) ||
      (this.filterToggle && this.filterToggle === this.MONTH && this.monthFilter === new Date().getMonth() + 1)
    ) {
      this.refreshTile()
    }
  }
}
</script>
